import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { apiGetPost } from "../lib/api";
import SEO from "../components/SEO";
import { ChevronRight } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const renderBody = (body) => {
  // Minimal "markdown lite": paragraphs split by blank lines, simple heading + list support.
  // We deliberately avoid a full markdown library to keep the bundle lean — the admin
  // can use line breaks for paragraphs and "## " / "- " prefixes for headings + bullets.
  const blocks = (body || "").split(/\n{2,}/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("## ")) {
      return (
        <h2 key={i} className="font-display text-2xl font-semibold text-[#0a0a0a] mt-10 mb-3 tracking-tight">
          {trimmed.slice(3).trim()}
        </h2>
      );
    }
    if (trimmed.startsWith("# ")) {
      return (
        <h2 key={i} className="font-display text-3xl font-semibold text-[#0a0a0a] mt-10 mb-3 tracking-tight">
          {trimmed.slice(2).trim()}
        </h2>
      );
    }
    if (trimmed.split("\n").every((l) => l.trim().startsWith("- "))) {
      return (
        <ul key={i} className="my-4 space-y-2 list-none">
          {trimmed.split("\n").map((li, j) => (
            <li key={j} className="flex gap-3 text-gray-700 leading-relaxed">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#b91c1c] flex-shrink-0" />
              <span>{li.replace(/^-\s*/, "")}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="text-gray-700 leading-relaxed my-4 font-light">
        {trimmed}
      </p>
    );
  });
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    apiGetPost(slug)
      .then((p) => mounted && setPost(p))
      .catch(() => mounted && setNotFound(true))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [slug]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-sm text-gray-500">Loading…</div>;
  }
  if (notFound || !post) return <Navigate to="/blog" replace />;

  const seoTitle = post.metaTitle || post.title;
  const seoDescription = post.metaDescription || post.excerpt;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    datePublished: post.publishedAt || post.createdAt,
    dateModified: post.updatedAt || post.publishedAt || post.createdAt,
    author: { "@type": "Organization", name: post.author },
    image: post.coverImage || undefined,
    description: seoDescription,
    mainEntityOfPage: { "@type": "WebPage", "@id": `/blog/${post.slug}` },
  };

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={`/blog/${post.slug}`}
        image={post.coverImage}
        type="article"
        structuredData={structuredData}
      />
      <article className="max-w-3xl mx-auto px-4 lg:px-10 py-16">
        <nav className="text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-6 flex items-center flex-wrap">
          <Link to="/" className="hover:text-[#0a0a0a]">Home</Link>
          <ChevronRight className="h-3 w-3 mx-1" />
          <Link to="/blog" className="hover:text-[#0a0a0a]">Research Journal</Link>
          <ChevronRight className="h-3 w-3 mx-1" />
          <span className="text-[#0a0a0a] font-semibold truncate max-w-[200px]">{post.title}</span>
        </nav>

        <div className="text-[10px] tracking-[0.25em] uppercase text-[#b91c1c] mb-3" data-testid="blog-post-date">
          {formatDate(post.publishedAt || post.createdAt)}
        </div>
        <h1
          className="font-display text-4xl md:text-5xl font-semibold text-[#0a0a0a] leading-tight tracking-tight"
          data-testid="blog-post-title"
        >
          {post.title}
        </h1>
        <div className="mt-3 text-sm text-gray-500 font-light">By {post.author}</div>

        {post.coverImage && (
          <div className="mt-8 aspect-[16/9] bg-white border border-gray-100 overflow-hidden">
            <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="mt-10 prose-cw" data-testid="blog-post-body">
          {renderBody(post.body)}
        </div>

        <div className="mt-16 pt-8 border-t border-gray-200 flex items-center justify-between">
          <Link to="/blog" className="text-[11px] tracking-[0.2em] uppercase text-gray-500 hover:text-[#b91c1c]">
            ← All journal entries
          </Link>
          <Link to="/shop" className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#0a0a0a] hover:text-[#b91c1c]">
            Browse catalog →
          </Link>
        </div>
      </article>
    </>
  );
};

export default BlogPost;
